import { createSlice } from '@reduxjs/toolkit';

import type { StatusEntity } from '@models';
import { nullStatusEntity } from '@models';
import {
  createEntityAction,
  createStatusEntityReducers,
  getEntityActions,
} from '@store/common';
import raiseToast from '@components/toast';
import type {
  GetLimitsActionParams,
  GetLimitsResponse,
  SystemResourceLimit,
} from '@features/limits/limits.models';
import { systemLimitDisplayNames } from '@features/limits/limits.models';

const raiseLimitReachedToast = (
  limit: SystemResourceLimit,
  resolution: string,
  exceeded: boolean,
) => {
  raiseToast({
    toastId: `limit-exceeded-${limit.name}`,
    title: `System Limit ${exceeded ? 'Exceeded' : 'Reached'}: ${systemLimitDisplayNames[limit.name!]}`,
    message: `The system has ${exceeded ? 'exceeded' : 'reached'} the allowed limit of ${limit.limitValue} for ${systemLimitDisplayNames[limit.name!]} (current value: ${limit.currentValue}). ${resolution}`,
    level: 'error',
    icon: 'times circle',
  });
};

const raiseLimitApproachingToast = (
  limit: SystemResourceLimit,
  resolution: string,
) => {
  raiseToast({
    toastId: `limit-approaching-${limit.name}`,
    title: `Approaching System Limit: ${systemLimitDisplayNames[limit.name!]}`,
    message: `The system is approaching the allowed limit of ${limit.limitValue} for ${systemLimitDisplayNames[limit.name!]} (current value: ${limit.currentValue}). ${resolution}`,
    level: 'warning',
    icon: 'warning sign',
  });
};

const checkLimits = (
  limits: SystemResourceLimit[],
  isAdmin: boolean,
  crpEnabled: boolean,
) => {
  limits
    .map(item => ({
      ...item,
      limitReached: item.limitValue
        ? (item.currentValue ?? 0) >= item.limitValue
        : false,
      warningReached: item.warningValue
        ? (item.currentValue ?? 0) >= item.warningValue
        : false,
    }))
    .filter(item => item.limitReached || item.warningReached)
    .forEach(limit => {
      let resolution = `You can resolve this issue by deleting ${systemLimitDisplayNames[limit.name!]} until you are under the ${limit.limitReached ? 'limit' : 'warning threshold'}.`;
      if (!crpEnabled) {
        resolution = isAdmin
          ? 'Please check your system limits to resolve this issue.'
          : 'Please contact your administrator to resolve this issue.';
      }
      if (limit.limitReached) {
        raiseLimitReachedToast(limit, resolution, limit.limitExceeded ?? false);
      } else {
        raiseLimitApproachingToast(limit, resolution);
      }
    });
};

const entities = {
  getSystemLimits: createEntityAction<GetLimitsActionParams, GetLimitsResponse>(
    {
      sliceName: 'limits',
      servicePath: () => `/service/limits`,
      onSuccess: (response, params, thunkApi) => {
        const { account } = thunkApi.getState().auth;
        const isAdmin = account?.isAdmin ?? false;
        checkLimits(response.body.data, isAdmin, params.crpEnabled);
      },
    },
  ),
};

export const { getSystemLimits } = getEntityActions(entities);

export type LimitsState = StatusEntity<SystemResourceLimit[]>;

export const initialState: LimitsState = nullStatusEntity;

const limitsSlice = createSlice({
  name: 'limits',
  initialState,
  reducers: {},
  extraReducers: builder => {
    createStatusEntityReducers(entities, builder);
  },
});

export default limitsSlice.reducer;
